import * as React from "react";
import { ReactSketchCanvas } from "react-sketch-canvas";
import {useEffect, useRef, useState } from 'react';
import { Text, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import {useDispatch, useSelector} from "react-redux";
import {setSketchPaths} from "../../../store/user.slice";
import {setAnswer} from '../../../store/questionnaire.slice';
import translate from '../../../services/localization';
import DeviceInfo from "react-native-device-info";
import {markdownToHtml, stripTags} from "../../../services/utils";
import RenderHtml from "react-native-render-html";
import ModalInfo from "../modalInfo";
import {theme} from '../../../config';

export default function SketchCanvasInput({item, imageUrl}) {
    const dispatch = useDispatch();
    const canvas = useRef(null);
    const [isErasing, setIsErasing] = useState(false);
    const { sketchPaths } = useSelector(state => state.User);
    const MAX_STROKE_WIDTH = 20;
    const STROKE_WIDTH_INCREMENT = 5;
    const [strokeWidth, setStrokeWidth] = useState(5);  // Set minimum stroke width
    const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 });  // Default canvas dimensions

    const imageBackground = new Image();
    const {width, height} = useWindowDimensions();
    imageBackground.crossOrigin = "anonymous";
    imageBackground.src = imageUrl;
    useEffect(() => {
        console.log('SketchCanvasInput', `image url: ${imageUrl}`);
        // Load only once imageUrl has changed/initialized
        imageBackground.onload = () => {
            const imageWidth = imageBackground.width;
            const imageHeight = imageBackground.height;

            // max allowed size (60% of window)
            const maxWidth = width * 0.6;
            const maxHeight = height * 0.6;

            // scale, keep image aspect ratio
            const widthScale = maxWidth / imageWidth;
            const heightScale = maxHeight / imageHeight;

            // pick the smaller to preserve aspect ratio
            const scale = Math.min(widthScale, heightScale);

            const canvasWidth = imageWidth * scale;
            const canvasHeight = imageHeight * scale;

            setCanvasDimensions({width: canvasWidth, height: canvasHeight});
        };
        handleLoad();
    }, [imageUrl]);

    const handleUndo = () => {
        canvas.current.undo();
        // Canvas update cannot be waited to be finished because it's
        // a synchronous call that triggers an internal async state update,
        // but it does not return a Promise. Add a timeout as a workaround.
        setTimeout(() => {
            updateStoredStrokes().then();
        }, 500);
    };

    const handleRedo = () => {
        canvas.current.redo();
        setTimeout(() => {
            updateStoredStrokes().then();
        }, 500);
    };

    const handleClear = () => {
        canvas.current.clearCanvas();
        setTimeout(() => {
            updateStoredStrokes().then();
        }, 500);
    };

    const toggleEraser = () => {
        const next = !isErasing;
        setIsErasing(next);
        canvas.current.eraseMode(next);
    };

    const increaseStrokeWidth = () => {
        if(strokeWidth >= MAX_STROKE_WIDTH){
            setStrokeWidth(STROKE_WIDTH_INCREMENT);
        } else {
            setStrokeWidth(prev => prev + STROKE_WIDTH_INCREMENT);
        }
    };

    const handleSave = async () => {
        const sketchPathData = await canvas.current.exportPaths();
        dispatch(setSketchPaths({
            linkId: item.linkId,
            paths: sketchPathData
        }));
    };

    const handleLoad = () => {
        if (sketchPaths) {
            console.log('SketchCanvasInput', 'Stored strokes found! Load stored data...', item.linkId, sketchPaths?.[item.linkId]);
            canvas.current.loadPaths(sketchPaths?.[item.linkId] ?? []);
        }   else {
            console.warn('SketchCanvasInput', 'No valid sketch paths to load');
        }

    };

    /**
     * Export current strokes/paths on canvas and store it in Redux
     * @returns {Promise<void>}
     */
    const updateStoredStrokes = async () => {
        const sketchPathData = await canvas.current.exportPaths();
        dispatch(setSketchPaths({
            linkId: item.linkId,
            paths: sketchPathData
        }));

        // Generates base64 image
        /**
         *  type: "png" | "jpeg",             // image type
         *  transparent?: boolean,            // whether to make background transparent (false = include background)
         *  imageType?: "png" | "jpeg",
         *  includeBackgroundImage?: boolean  // include the background image
         */

        const base64Image = await canvas.current.exportImage('png', true, 'png', true);
        dispatch(setAnswer({
            linkId: item.linkId,
            answer: {
                [`valueBase64File`]:
                    base64Image,
            },
        }))
    };

    const source = {
        html: '<div style="font-weight: bold; font-size: ' + (DeviceInfo.isTablet() ? '1.75em' : '1.2em') + '">' + markdownToHtml(item.text) + '</div>'
    };
    return (
        <View style={styles.container}>
            {/* title */}
            <RenderHtml
                contentWidth={width}
                source={source}
            />
            {!!item.fieldAnnotation && !!stripTags(item.fieldAnnotation) &&
                <ModalInfo
                    infoText={stripTags(item.fieldAnnotation)}
                />
            }
            <View style={styles.buttonsRow}>
                <TouchableOpacity
                    style={[styles.button, isErasing && styles.activeButton]}
                    onPress={toggleEraser}
                >
                    <Text style={{color: '#fff'}}>{translate('survey').sketchEraser}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                    style={styles.strokeButton}
                    onPress={increaseStrokeWidth}
                >
                    <View
                        style={[
                            styles.strokePreview,
                            {
                                width: strokeWidth,
                                height: strokeWidth,
                                borderRadius: strokeWidth / 2,
                            },
                        ]}
                    />
                </TouchableOpacity>
                <TouchableOpacity style={styles.button} onPress={handleUndo}>
                    <Text style={{color: '#fff'}}>{translate('survey').sketchUndo}</Text>
                </TouchableOpacity>
                {/*<TouchableOpacity style={styles.button} onPress={handleRedo}>*/}
                {/*    <Text>Redo</Text>*/}
                {/*</TouchableOpacity>*/}
                <TouchableOpacity style={styles.button} onPress={handleClear}>
                    <Text style={{color: '#fff'}}>{translate('survey').sketchClear}</Text>
                </TouchableOpacity>

                {/*<TouchableOpacity style={styles.button} onPress={handleExport}>*/}
                {/*    <Text>DEBUG: Export</Text>*/}
                {/*</TouchableOpacity>*/}

                {/*<TouchableOpacity style={styles.button} onPress={handleSave}>*/}
                {/*    <Text>Save</Text>*/}
                {/*</TouchableOpacity>*/}
                {/*<TouchableOpacity style={styles.button} onPress={handleLoad}>*/}
                {/*    <Text>Load</Text>*/}
                {/*</TouchableOpacity>*/}
            </View>
            <View style={[{ width : canvasDimensions.width, height : canvasDimensions.height }, styles.canvasWrapper]}>
                <ReactSketchCanvas
                    ref={canvas}
                    style={styles.canvas}
                    strokeWidth={strokeWidth}
                    onStroke={updateStoredStrokes}
                    exportWithBackgroundImage={true}
                    backgroundImage={imageBackground.src}
                    strokeColor={"blue"}
                />
            </View>
        </View>
    );
}

const styles = {
    canvas : {
        width: "100%",
        height: "100%",
    },
    canvasWrapper: {
        borderWidth: 1,
        borderColor: "#9c9c9c",
        borderRadius: 5,
        overflow: "hidden",
        marginBottom: 16,
    },
    container : {
        flex: 1,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center"
    },
    buttonsRow: {
        flexDirection: "row",
        justifyContent: "center",
        gap: 12,
        paddingTop: 20,
        paddingBottom: 20,
    },
    button: {
        paddingVertical: 8,
        paddingHorizontal: 12,
        backgroundColor: theme.colors.success,
        borderRadius: 5,
    },
    strokeButton: {
        width: 30,
        height: 30,
        borderRadius: 15,
        backgroundColor: theme.colors.success,
        justifyContent: 'center',
        alignItems: 'center',
    },
    strokePreview: {
        backgroundColor: '#fff',
    },
    activeButton: {
        backgroundColor: '#4d7fc4',
    },
};
